import Button from 'beam/dist/Button'
import { withStyles } from 'beam/dist/styles'
import TextField from 'beam/dist/TextField'
import { Form, Formik } from 'formik'
import { func, shape, string } from 'prop-types'
import { Component } from 'react'
import { withTranslation } from 'react-i18next'
import { compose } from 'redux'
import { PasswordStrength } from '../Password/Dialog'
import PasswordStrengthIndicator from '../../Common/PasswordStrengthIndicator'

class ResetPasswordForm extends Component {
  static propTypes = {
    checkPasswordStrength: func,
    classes: shape({ root: string.isRequired, buttonRow: string.isRequired }).isRequired,
    onReset: func,
    resetPassword: func,
    t: func.isRequired,
    uuid: string.isRequired,
  }

  static defaultProps = {
    onReset: () => {},
  }

  static defaultFormValues = {
    email: '',
    password: '',
    confirmedPassword: '',
  }

  static styles = {
    root: {},
    buttonRow: {
      display: 'flex',
      justifyContent: 'right',
    },
  }

  state = {
    // unable to perform async validation with formik
    // so the validation logic was extracted to the
    // change handler (`updatePassword`) and these
    // validation state variables
    isPasswordStrengthValidating: false,
    isPasswordStrengthValid: false,
    password: '',
    passwordStrength: PasswordStrength.EMPTY,
  }

  updatePassword(handleChange) {
    return async (event, ...restArgs) => {
      handleChange(event, ...restArgs)
      const password = event.target.value
      this.setState({
        isPasswordStrengthValid: false,
        isPasswordStrengthValidating: true,
        password,
      })
      const { strength, valid } = await this.props.checkPasswordStrength(password)
      this.setState(prevState => {
        // prevent API race condition by confirming that the strength data was from the same password as the one that was most recently typed
        if (prevState.password !== password) {
          return prevState
        }
        return {
          isPasswordStrengthValid: valid,
          isPasswordStrengthValidating: false,
          passwordStrength: strength,
        }
      })
    }
  }

  render() {
    const { classes, resetPassword, onReset, t, uuid } = this.props
    const { isPasswordStrengthValid, isPasswordStrengthValidating, passwordStrength } = this.state
    return (
      <Formik
        initialValues={ResetPasswordForm.defaultFormValues}
        validate={({ email, password, confirmedPassword }) => {
          const errors = {}
          if (!email) {
            errors.email = t('required')
          }
          if (!password) {
            errors.password = t('required')
          } else if (!isPasswordStrengthValidating && !isPasswordStrengthValid) {
            errors.password = passwordStrength
          }
          if (password !== confirmedPassword) {
            errors.confirmedPassword = t('passwordMatch')
          }
          return errors
        }}
        onSubmit={async ({ email, password }, { setFieldError, setSubmitting }) => {
          try {
            setSubmitting(true)
            await resetPassword(uuid, email, password)
            onReset()
          } catch (e) {
            if (e && e.response && e.response.data && e.response.data.errorMessage) {
              setFieldError('email', e.response.data.errorMessage)
            } else {
              throw e
            }
          } finally {
            setSubmitting(false)
          }
        }}
        render={({ errors, handleChange, handleBlur, isSubmitting, isValid, dirty, isValidating, touched, values }) => (
          <Form>
            <TextField
              type="email"
              name="email"
              value={values.email}
              label={t('confirmEmail')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
              disabled={isSubmitting}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              required
            />
            <TextField
              type="password"
              name="password"
              value={values.password}
              label={t('newPassword')}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
              disabled={isSubmitting}
              onChange={this.updatePassword(handleChange)}
              onBlur={handleBlur}
              fullWidth
              required
            />
            <PasswordStrengthIndicator strength={values.password ? passwordStrength : PasswordStrength.EMPTY} />
            <TextField
              type="password"
              name="confirmedPassword"
              value={values.confirmedPassword}
              label={t('confirmNewPassword')}
              error={Boolean(touched.confirmedPassword && errors.confirmedPassword)}
              helperText={touched.confirmedPassword && errors.confirmedPassword}
              disabled={isSubmitting}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              required
            />
            <div className={classes.buttonRow}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={isSubmitting || !isValid || !dirty || isValidating || isPasswordStrengthValidating}
              >
                {t('savePassword')}
              </Button>
            </div>
          </Form>
        )}
      />
    )
  }
}

export default compose(withStyles(ResetPasswordForm.styles), withTranslation())(ResetPasswordForm)
